// ABOUTME: <product-positioner> web component — collection PLP back-button scroll restoration
// (UTIL-PRODUCTPOSITIONER). Ports the live theme's ProductPositioner (sessionStorage scroll
// restoration): records the product path when a grid card is clicked, and on return to the
// collection (back/forward, including bfcache) scrolls the matching tile to `offset` px below the
// viewport top — the live theme's -200px anchor. Self-contained on the collection template; no PDP
// coupling. Renders nothing (host is display:none).

import { getScrollContainer } from '@theme/scroll-container';

const STORAGE_KEY = 'jf:plp:last-product';
const CARD_SELECTOR = '.product-grid__item, .product-card, li[data-product-id]';

class ProductPositioner extends HTMLElement {
  connectedCallback() {
    const parsed = parseInt(this.getAttribute('offset'), 10);
    this.offset = Number.isNaN(parsed) ? 200 : parsed;

    this.onClick = this.onClick.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onLoad = this.restore.bind(this);
    document.addEventListener('click', this.onClick, true);
    window.addEventListener('pageshow', this.onPageShow);

    // Restore after load so the browser's own scroll restoration (which fires around the load
    // event and would otherwise win) has already run.
    if (document.readyState === 'complete') {
      this.restore();
    } else {
      window.addEventListener('load', this.onLoad, { once: true });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick, true);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('load', this.onLoad);
  }

  onClick(event) {
    const link = event.target.closest('a[href*="/products/"]');
    if (!link || !link.closest(CARD_SELECTOR)) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, new URL(link.href, window.location.origin).pathname);
    } catch (e) {
      /* sessionStorage unavailable — restoration is a progressive enhancement */
    }
  }

  onPageShow(event) {
    if (event.persisted) this.restore();
  }

  restore() {
    let path;
    try {
      path = sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return;
    }
    if (!path) return;

    let cancelled = false;
    const cancel = () => {
      cancelled = true;
    };
    const inputs = ['wheel', 'touchstart', 'keydown'];
    inputs.forEach((t) => window.addEventListener(t, cancel, { once: true, passive: true }));

    const apply = () => {
      if (cancelled) return;
      const tile = this.findTile(path);
      if (!tile) return;
      // The desktop squeeze layout scrolls inside .page-wrapper, not the window — resolve the
      // real container through the shared adapter so restoration lands on every viewport.
      const container = getScrollContainer();
      const target = Math.max(0, tile.getBoundingClientRect().top + container.scrollTop - this.offset);
      // Instant jump (not the theme's smooth scroll): restoration should land, not animate.
      if (Math.abs(container.scrollTop - target) > 2) container.scrollTo({ top: target, behavior: 'instant' });
    };

    // Re-assert the anchor across a short window so the browser's post-load scroll restoration can't
    // override it and image-driven layout shifts are absorbed. Timers (not rAF) so it runs even when
    // frames are throttled; yields to real user scrolling; no-ops when the tile isn't on this page.
    apply();
    [80, 200, 400, 650, 900].forEach((ms) => setTimeout(apply, ms));
    setTimeout(() => inputs.forEach((t) => window.removeEventListener(t, cancel)), 950);
  }

  findTile(path) {
    for (const link of document.querySelectorAll('a[href*="/products/"]')) {
      let linkPath;
      try {
        linkPath = new URL(link.href, window.location.origin).pathname;
      } catch (e) {
        continue;
      }
      if (linkPath === path) return link.closest(CARD_SELECTOR) || link;
    }
    return null;
  }
}

if (!customElements.get('product-positioner')) {
  customElements.define('product-positioner', ProductPositioner);
}
